import { useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import type { Member } from '@ebalance/proto/bffuser/v1/user_directory_model_pb'
import { UpdateSubStepMemoRequestSchema } from '@ebalance/proto/bffuser/v1/workflow_case_service_pb'
import { useTranslation } from 'react-i18next'

import { HStack, TertiaryButton } from '@enechain/ecloud-designsystem'
import { FaFileLinesRegularFull } from '@enechain/ecloud-icons'

import { useErrorToast } from '~/hooks/useErrorToast'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { formatDateTimeFull } from '~/models/dateTime'
import { resolveMemberDisplayName } from '~/models/member'
import { MemoBadge } from '~/pages/WorkflowCaseDetail/components/MemoBadge'
import { SubStepMemoDialog } from '~/pages/WorkflowCaseDetail/components/SubStepMemoDialog'
import { useUpdateSubStepMemo } from '~/pages/WorkflowCaseDetail/hooks/useUpdateSubStepMemo'

type Props = {
  caseId: string
  bigStepDisplayOrder: number
  subStepDisplayOrder: number
  subStepName: string
  memo: string | undefined
  memoBy: string | undefined
  memoAt: Timestamp | undefined
  members: Member[]
  /** read-only ガードはメモ UI には適用しない (spec AC-WFW-005b)。 */
  isReadonly: boolean
}

/**
 * サブステップメモの常時表示パネル + 追加/削除導線 (spec AC-WFW-005b)。
 * workflow の完了・削除後もメモは表示・編集・削除可能 (isReadonly は無視)。
 */
export const SubStepMemoPanel = ({
  caseId,
  bigStepDisplayOrder,
  subStepDisplayOrder,
  subStepName,
  memo,
  memoBy,
  memoAt,
  members,
}: Props): React.ReactElement | null => {
  const showSuccessToast = useSuccessToast()
  const showErrorToast = useErrorToast()
  const { t } = useTranslation()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const { mutateAsync, isPending } = useUpdateSubStepMemo(caseId)

  const hasMemo = memo !== undefined && memo !== ''
  const writerName = resolveMemberDisplayName(members, memoBy) ?? '-'

  const dialog = isDialogOpen && (
    <SubStepMemoDialog
      bigStepDisplayOrder={bigStepDisplayOrder}
      caseId={caseId}
      initialMemo={memo}
      isOpen={isDialogOpen}
      members={members}
      memoAt={memoAt}
      memoBy={memoBy}
      onClose={() => {
        setIsDialogOpen(false)
      }}
      subStepDisplayOrder={subStepDisplayOrder}
      subStepName={subStepName}
    />
  )

  const handleDelete = async (): Promise<void> => {
    try {
      await mutateAsync(
        create(UpdateSubStepMemoRequestSchema, {
          workflowCaseId: caseId,
          bigStepDisplayOrder,
          subStepDisplayOrder,
          // memo unset (proto3 optional 未指定) = 削除
        }),
      )
      showSuccessToast({
        title: t('page:workflowCaseDetail.subStep.memo.deleteToast'),
      })
    } catch {
      showErrorToast({
        title: t('page:workflowCaseDetail.error.memoFailed'),
      })
    }
  }

  if (!hasMemo) {
    return (
      <>
        <TertiaryButton
          alignSelf="flex-end"
          onClick={() => {
            setIsDialogOpen(true)
          }}
          size="sm"
        >
          <FaFileLinesRegularFull />
          {t('page:workflowCaseDetail.subStep.memo.addButton')}
        </TertiaryButton>
        {dialog}
      </>
    )
  }

  return (
    <HStack justifyContent="flex-end" gap={1}>
      <MemoBadge
        /* サブステップメモは design のコンパクト表示 (480px 上限 + 1 行省略)。
           承認メモ (`ApprovalGateSection`) は幅上限を持たない別表現なので渡さない。 */
        compact
        date={
          memoAt !== undefined
            ? formatDateTimeFull(timestampDate(memoAt))
            : undefined
        }
        deletable
        editAriaLabel={t(
          'page:workflowCaseDetail.subStep.memo.viewEditAriaLabel',
        )}
        editable
        isPending={isPending}
        label={t('page:workflowCaseDetail.subStep.memo.writerLabel', {
          name: writerName,
        })}
        memo={memo}
        onDelete={handleDelete}
        onEdit={() => {
          setIsDialogOpen(true)
        }}
      />
      {dialog}
    </HStack>
  )
}
